import { useMachine } from '@xstate/react'
import { PageId } from 'constants/pages'
import { getWoofzWoofySteps } from 'nove8/cohorts/woofz_woofy1/helpers/getWoofzWoofySteps'
import {
  Cohort,
  WOOFZ_COACHING,
  WOOFZ_VC6,
} from 'nove8/constants/root-constants'
import { STEPS_MAP } from 'nove8/constants/steps-map'
import { XSTATE_CONFIGS_MAP } from 'nove8/constants/xstate-configs-map'
import { IFlowRouterContext } from 'nove8/contexts/flowRouterContext'
import { filterStepsByDogProfile } from 'nove8/helpers/filterPagesAccordingUserAnswers'
import { getWoofzCoachingSteps } from 'nove8/helpers/getWoofzCoachingSteps'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createMachine } from 'xstate'
import { useShallow } from 'zustand/shallow'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandStore } from 'zustandGlobalStore'

export const useCreateXStateMachine = (): {
  flowRouterContext: IFlowRouterContext
  isPageLoaded: boolean
} => {
  const {
    cohort,
    currentPageId,
    dogProfile,
    dogsProfile,
    firebaseRemoteConfig,
  } = useZustandStore(
    useShallow((state) => ({
      cohort: state.lead?.cohort || state.initialCohort,
      currentPageId: state.currentPageId,
      dogProfile: state.dogProfile,
      dogsProfile: state.dogsProfile,
      firebaseRemoteConfig: state.firebaseRemoteConfig,
    })),
  )
  const [isPageLoaded, setPageLoaded] = useState(false)

  const steps = useMemo(() => {
    if (cohort === WOOFZ_COACHING) {
      return getWoofzCoachingSteps(dogsProfile)
    }

    if (cohort === WOOFZ_VC6) {
      return filterStepsByDogProfile(STEPS_MAP[WOOFZ_VC6], dogProfile)
    }

    if (cohort === Cohort.WOOFZ_WOOFY1) {
      return getWoofzWoofySteps(firebaseRemoteConfig)
    }

    return STEPS_MAP[cohort]
  }, [cohort, dogProfile, dogsProfile, firebaseRemoteConfig])

  useEffect(() => {
    setPageLoaded(false)
  }, [currentPageId])

  useEffect(() => {
    useZustandSessionStore.setState({
      isFirstPageLoad: true,
      isStepBack: false,
    })
  }, [])

  const [current, send] = useMachine(() =>
    createMachine({
      id: 'flowRouter',
      initial: 'initial',
      states: XSTATE_CONFIGS_MAP[cohort](steps),
      predictableActionArguments: true,
    }),
  )

  useEffect(() => {
    const pageIdFromStateMachine = current.value as PageId
    const pageIndexFromStateMachine = steps.findIndex(
      (page) => page.pageId === (current.value as PageId),
    )

    useZustandStore.setState({
      currentPageId: pageIdFromStateMachine,
      currentPageIndex: pageIndexFromStateMachine,
      currentPageNumber:
        pageIndexFromStateMachine >= 0 ? pageIndexFromStateMachine + 1 : 0,
    })
  }, [current.value, steps])

  const goToNextStep = useCallback(() => {
    setPageLoaded(false)
    useZustandSessionStore.setState({
      isStepBack: false,
      isFirstPageLoad: false,
    })
    send('NEXT_FLOW_STEP')
  }, [send])

  const goToPreviousStep = useCallback(() => {
    useZustandSessionStore.setState({
      isStepBack: true,
      isFirstPageLoad: false,
    })
    send('PREVIOUS_FLOW_STEP')
  }, [send])

  const goToSkipOneStepBack = useCallback(() => {
    useZustandSessionStore.setState({
      isStepBack: true,
      isFirstPageLoad: false,
    })
    send('SKIP_ONE_STEP_BACK')
  }, [send])

  const goToSkipOneStep = useCallback(() => {
    useZustandSessionStore.setState({
      isStepBack: false,
      isFirstPageLoad: false,
    })
    send('SKIP_ONE_STEP')
  }, [send])

  return {
    flowRouterContext: {
      onNextStep: goToNextStep,
      onSkipOneStep: goToSkipOneStep,
      onPreviousStep: goToPreviousStep,
      onSkipOneStepBack: goToSkipOneStepBack,
      onPageLoad: () => setPageLoaded(true),
    },
    isPageLoaded,
  }
}
